<template>
  <SectionY :padding="data.padding?.value" class="mb-20 sm:mb-24 xl:mb-28">
    <ContainerX is-max>
      <transition name="fade" mode="out-in">
        <!-- SUCCESS MESSAGE -->
        <div
          v-if="state === 'submitted'"
          key="success"
          class="flex w-full justify-center"
        >
          <p class="flex max-w-text items-center space-x-3 text-center text-md">
            <CheckmarkIcon class="w-8 text-main-green" />
            <span> {{ data.success_message }}</span>
          </p>
        </div>

        <!-- FORM -->
        <Form
          v-else
          key="form"
          ref="form"
          v-slot="{ errors }"
          novalidate
          class="grid grid-cols-4 gap-x-4 gap-y-2 sm:grid-cols-12 sm:gap-x-8 sm:gap-y-3 xl:gap-x-12"
          @submit="submit"
        >
          <template
            v-for="inputField in data?.registration_form?.fields"
            :key="inputField.handle"
          >
            <!-- INPUT FIELDS -->
            <InputField
              v-if="
                inputField.type !== 'toggle' &&
                inputField.handle !== 'country' &&
                inputField.handle !== 'phone'
              "
              :field="inputField"
              :rules="data?.registration_form?.rules[inputField.handle]"
              :label="inputField.display"
              :error="errors[inputField.handle]"
              class="mb-3"
              :value="registerFrom[inputField.handle]"
              @field-input="registerFrom[inputField.handle] = $event"
            />

            <!-- PHONE -->
            <InputPhone
              v-if="inputField.handle === 'phone' && countries"
              :countries="countries"
              :field="inputField"
              :label="inputField.display"
              :error="errors[inputField.handle]"
              class="mb-3"
              :rules="data?.registration_form?.rules[inputField.handle]"
              :value="registerFrom[inputField.handle]"
              @input="registerFrom[inputField.handle] = $event"
            />

            <!-- COUNTRY -->
            <Field
              v-if="inputField.handle === 'country'"
              v-slot="{ field, errorMessage }"
              :value="registerFrom[inputField.handle]"
              :name="inputField.display"
              rules="required"
              @input="
                registerFrom[
                  inputField.handle
                ] = `${$event.iso2} ${$event.title}`
              "
            >
              <InputCountries
                v-if="inputField.handle === 'country' && countries"
                :countries="countries"
                :field="inputField"
                v-bind="field"
                :label="inputField.display"
                :error="errorMessage"
                class="mb-3"
              />
            </Field>

            <!-- PRIVACY POLICY -->
            <div
              v-if="inputField.handle === 'accept_private_policy'"
              class="col-span-4 my-9 justify-self-center sm:col-span-12"
            >
              <Field
                v-slot="{ field }"
                v-model="registerFrom[inputField.handle]"
                :name="inputField.handle"
                type="checkbox"
                :label="inputField.display"
                :value="true"
                rules="required"
              >
                <label class="flex cursor-pointer items-center space-x-3">
                  <div
                    class="relative h-5 w-5 overflow-hidden rounded-[0.25rem] border-2 border-main-blue focus-within:ring-2 focus-within:ring-offset-2"
                  >
                    <input
                      type="checkbox"
                      class="focus-default absolute inset-0 opacity-0"
                      :name="inputField.handle"
                      v-bind="field"
                      :value="true"
                    />
                    <transition name="bounce">
                      <div
                        v-if="registerFrom[inputField.handle]"
                        class="absolute inset-0 flex items-center justify-center bg-main-blue"
                      >
                        <CheckmarkIcon class="absolute w-3 text-white" />
                      </div>
                    </transition>
                  </div>
                  <span
                    class="text mt-1"
                    v-html="data.checkbox_text_and_link"
                  />
                </label>
              </Field>
              <transition name="fade">
                <p
                  v-if="errors[inputField.handle]"
                  class="mt-1 text-xs text-red-500"
                >
                  {{ errors[inputField.handle] }}
                </p>
              </transition>
            </div>
          </template>

          <!-- HONEY POT -->
          <input
            v-model="registerFrom.honigchuebel"
            class="hidden"
            name="honigchuebel"
          />
          <div
            class="relative col-span-4 my-12 w-fit justify-self-center sm:col-span-12"
          >
            <AppButton
              type="submit"
              :class="{ 'pointer-events-none': Object.keys(errors).length }"
              :disabled="Object.keys(errors).length"
            >
              {{ data.submit_button_text }}
            </AppButton>
            <!-- SPINNER -->
            <transition name="fade">
              <div
                v-if="state === 'loading'"
                class="absolute -bottom-full left-1/2 -translate-x-1/2"
              >
                <SpinIcon
                  class="h-7 w-7 animate-spin text-second-blue-300 sm:h-10 sm:w-10"
                />
              </div>
            </transition>
          </div>
        </Form>
      </transition>

      <!-- ERROR MESSAGE -->
      <transition name="fade">
        <div
          v-if="state === 'error'"
          key="error"
          class="flex w-full justify-center"
        >
          <p class="flex max-w-text items-center space-x-3 text-center text-md">
            <CrossIcon class="w-7 text-red-500" />
            <span> {{ data.error_message }}</span>
          </p>
        </div>
      </transition>
    </ContainerX>
  </SectionY>
</template>

<script setup>
import { Form, Field } from 'vee-validate'

const route = useRoute()

defineProps({
  data: { type: Object, required: true },
})

const registerFrom = ref({})

// get form data from url query param an fill form with it
let teaserRegistrationForm = decodeURIComponent(route.query.teaser_registration)
if (teaserRegistrationForm && teaserRegistrationForm !== 'undefined') {
  teaserRegistrationForm = JSON.parse(
    decodeURIComponent(route.query.teaser_registration)
  )

  registerFrom.value = {
    ...teaserRegistrationForm,
  }
}

const countries = ref([])

const state = ref('idle')

const response = await useAsyncGql({
  operation: 'Countries',
})

if (!response.data?.value?.terms?.data) {
  console.error('could not fetch countries in component input-countries')
}

countries.value = response.data?.value?.terms?.data

async function submit(values, { resetForm }) {
  state.value = 'loading'

  const config = useRuntimeConfig()

  try {
    await $fetch(`${config.public.API_URL}/!/forms/registration`, {
      method: 'POST',
      body: registerFrom.value,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })

    await nextTick()
    registerFrom.value = {}

    state.value = 'submitted'

    resetForm()
  } catch (e) {
    state.value = 'error'
    console.error(e)
  }
}
</script>
